import React, { useState } from "react";
import {
  Box, Typography, Grid, TextField, Paper, Checkbox,
  FormControlLabel, Button, IconButton, Avatar, Stack, CircularProgress
} from "@mui/material";
import {
  AddCircleOutline,
  DeleteOutline,
  CloudUpload as CloudUploadIcon
} from "@mui/icons-material";
import { storage } from "../../../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

function GuardianInfo({ form, handleChange }) {
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const guardians = form.guardians || [];

  // --- Upload Logic ---
  const handlePhotoUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingIndex(index);
    try {
      const storageRef = ref(storage, `guardian_photos/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // Update specific guardian's photo in the array
      const updatedList = [...guardians];
      updatedList[index] = { ...updatedList[index], photo: downloadURL };
      handleChange("guardians", updatedList);
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setUploadingIndex(null);
    }
  };

  const updateGuardian = (index, field, value) => {
    const updatedList = [...guardians];
    updatedList[index] = { ...updatedList[index], [field]: value };
    handleChange("guardians", updatedList);
  };

  const addGuardian = () => {
    const newG = { name: "", relationship: "", photo: "", mobile: "", email: "" };
    handleChange("guardians", [...guardians, newG]);
  };

  const removeGuardian = (index) => {
    const updatedList = guardians.filter((_, i) => i !== index);
    handleChange("guardians", updatedList);
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" bgcolor="#f4f6f8" p={2}>
      <Paper sx={{ p: 3, width: "100%", maxWidth: "1200px" }}>

        {/* HEADER */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ background: "teal", color: "#fff", p: 1, mb: 3, borderRadius: 1 }}>
          <Typography fontWeight="bold">5) Guardian's Information</Typography>
          <Button variant="contained" color="secondary" startIcon={<AddCircleOutline />} onClick={addGuardian}>
            Add Guardian
          </Button>
        </Stack>

        {guardians.map((guardian, index) => (
          <Box key={index} sx={{ mb: 2, p: 1, border: "2px dashed #teal", borderRadius: 2, bgcolor: "#fff" }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h6" color="teal">Guardian #{index + 1}</Typography>
              <IconButton color="error" onClick={() => removeGuardian(index)}><DeleteOutline /></IconButton>
            </Stack>

            <Grid className="row" spacing={3}>
              {/* LEFT: FORM FIELDS */}
              <Grid className="col-sm-9 mb-1">
                <Grid className="row" spacing={2}>
                  <Grid className="col-sm-4 mb-2" item xs={12} sm={8}>
                    <TextField fullWidth label="Name" size="small" value={guardian.name || ""} onChange={(e) => updateGuardian(index, "name", e.target.value)} />
                  </Grid>
                  <Grid className="col-sm-4 mb-2" item xs={12} sm={4}>
                    <TextField fullWidth label="Relationship" size="small" value={guardian.relationship || ""} onChange={(e) => updateGuardian(index, "relationship", e.target.value)} />
                  </Grid>
                  <Grid className="col-sm-4 mb-2" item xs={12} sm={9}>
                    <TextField fullWidth label="Residential Address" size="small" value={guardian.address || ""} onChange={(e) => updateGuardian(index, "address", e.target.value)} />
                  </Grid>
                  <Grid className="col-sm-4 mb-2" item xs={12} sm={3}>
                    <TextField fullWidth label="Pin" size="small" value={guardian.pin || ""} onChange={(e) => updateGuardian(index, "pin", e.target.value)} />
                  </Grid>
                  <Grid className="col-sm-4 mb-2" item xs={12} sm={6}>
                    <TextField fullWidth label="Email" size="small" value={guardian.email || ""} onChange={(e) => updateGuardian(index, "email", e.target.value)} />
                  </Grid>
                  <Grid className="col-sm-4 mb-2" item xs={12} sm={6}>
                    <TextField fullWidth label="Mobile" size="small" value={guardian.mobile || ""} onChange={(e) => updateGuardian(index, "mobile", e.target.value)} />
                  </Grid>
                </Grid>
              </Grid>

              {/* RIGHT: PHOTO UPLOAD FOR THIS GUARDIAN */}
              <Grid className="col-sm-3 mb-1">
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', border: '1px solid #ddd', p: 1, borderRadius: 2 }}>
                  <Avatar
                    variant="rounded"
                    src={guardian.photo}
                    sx={{ width: 120, height: 140, mb: 1, border: '1px solid #eee' }}
                  >
                    {uploadingIndex === index ? <CircularProgress size={20} /> : "Photo"}
                  </Avatar>
                  <Button
                    variant="outlined"
                    component="label"
                    size="small"
                    startIcon={<CloudUploadIcon />}
                    disabled={uploadingIndex !== null}
                  >
                    {uploadingIndex === index ? "Uploading..." : "Upload"}
                    <input type="file" hidden accept="image/*" onChange={(e) => handlePhotoUpload(e, index)} />
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        ))}

        {/* 6) CONTACT PREFERENCE */}
        <Box sx={{ background: "teal", color: "#fff", p: 1, mt: 4, mb: 2 }}>
          <Typography fontWeight="bold">6) Point of Contact Preference</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" fontWeight="bold">Mobile Preference</Typography>
            <FormControlLabel control={<Checkbox checked={form.mobilePrefMother || false} onChange={(e) => handleChange("mobilePrefMother", e.target.checked)} />} label="Mother" />
            <FormControlLabel control={<Checkbox checked={form.mobilePrefFather || false} onChange={(e) => handleChange("mobilePrefFather", e.target.checked)} />} label="Father" />
            <FormControlLabel control={<Checkbox checked={form.mobilePrefGuardian || false} onChange={(e) => handleChange("mobilePrefGuardian", e.target.checked)} />} label="Guardian" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" fontWeight="bold">Email Preference</Typography>
            <FormControlLabel control={<Checkbox checked={form.emailPrefMother || false} onChange={(e) => handleChange("emailPrefMother", e.target.checked)} />} label="Mother" />
            <FormControlLabel control={<Checkbox checked={form.emailPrefFather || false} onChange={(e) => handleChange("emailPrefFather", e.target.checked)} />} label="Father" />
            <FormControlLabel control={<Checkbox checked={form.emailPrefGuardian || false} onChange={(e) => handleChange("emailPrefGuardian", e.target.checked)} />} label="Guardian" />
          </Grid>
        </Grid>

        {/* 7) EMERGENCY CONTACT */}
        <Box sx={{ background: "teal", color: "#fff", p: 1, mt: 4, mb: 2 }}>
          <Typography fontWeight="bold">7) Emergency Contact Information</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Friend/Relative Name" size="small" value={form.emergencyName || ""} onChange={(e) => handleChange("emergencyName", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth label="Mobile" size="small" value={form.emergencyMobile || ""} onChange={(e) => handleChange("emergencyMobile", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth label="Landline" size="small" value={form.emergencyLandline || ""} onChange={(e) => handleChange("emergencyLandline", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Family Physician" size="small" value={form.doctorName || ""} onChange={(e) => handleChange("doctorName", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth label="Doctor Mobile" size="small" value={form.doctorMobile || ""} onChange={(e) => handleChange("doctorMobile", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth label="Doctor Landline" size="small" value={form.doctorLandline || ""} onChange={(e) => handleChange("doctorLandline", e.target.value)} />
          </Grid>
        </Grid>


      </Paper>
    </Box>
  );
}

export default GuardianInfo;